import { MaxLength, Min } from '@fareharbor-com/beacon-vue/validators';
import {
  toCamelCase,
  toKebabCase,
  toSnakeCase,
} from '../../../website/static/js/services/convert';
import { track, addSuperProperty, enableMixpanel } from '../scripts/mixpanel';

export const EMPTY_ERRORS_FOR_FIELDS = {
  signatureCanvas: '',
};

const isObject = (data) =>
  typeof data === 'object' && !Array.isArray(data) && data !== null;

const transformKeys = (data, transformFunction) => {
  if (isObject(data)) {
    Object.keys(data).forEach((key) => {
      const value = data[key];
      delete data[key];

      const newKey = transformFunction(key);
      data[newKey] = transformKeys(value, transformFunction);
    });
  }

  return data;
};

export const fetchWaiver = async (companiesShortname, bookingUuid) => {
  try {
    const response = await fetch(
      `/api/v1/companies/${companiesShortname}/fh-waivers/signature/${bookingUuid}/`,
    );
    const parsedData = await response.json();
    const data = transformKeys(parsedData, toCamelCase);

    if (data.redirectToRebookingUuid) {
      return { data, isRebooked: true };
    }

    if (data.company.processorCountry) {
      enableMixpanel(data.company.processorCountry);
    }

    if (data.waiver) {
      addSuperProperty({
        'fh-waiver-form-waiver-internal-name': data.waiver.internalName,
        'fh-waiver-form-waiver-id': data.waiver.uuid,
      });
    }

    const mxData = {
      'client-policy': !!data.company.policies,
    };

    if (response.status === 403) {
      mxData['reached-limit'] = true;
      track('fh-waiver-form-view', mxData);

      return { data, isReachedLimit: true };
    }

    if (response.status !== 200) {
      // ToDo: Need to handle edge cases
      track('fh-waiver-form-view', { 'server-error': true });

      throw new Error('Something went wrong');
    }

    mxData['reached-limit'] = false;
    mxData['signature-type'] = toKebabCase(data.waiver.signatureType);
    mxData['phone-number-enabled'] = data.waiver.isPhoneNumberEnabled;
    mxData['address-enabled'] = data.waiver.isAddressEnabled;
    mxData['dob-enabled'] = data.waiver.isDobEnabled;
    mxData['minors-enabled'] = data.waiver.isMinorsEnabled;
    mxData['additional-participants-enabled'] =
      data.waiver.isAdditionalParticipantsEnabled;

    track('fh-waiver-form-view', mxData);

    return { data };
  } catch (e) {
    track('fh-waiver-form-view', { 'not-found': true });

    return { isNotFound: true };
  }
};

export const signWaiver = async (companiesShortname, bookingUuid, formData) => {
  const mxData = {
    'marketing-checkbox': formData.isMarketingChecked,
    'minors-checkbox': formData.isMinorChecked,
    'minors-count': formData.minors.length,
    'sections-count': formData.sectionAcknowledgements?.length || 0,
    successful: false,
  };

  try {
    const body = JSON.stringify(transformKeys(formData, toSnakeCase));
    const response = await fetch(
      `/api/v1/companies/${companiesShortname}/fh-waivers/signature/${bookingUuid}/`,
      {
        method: 'post',
        body,
        headers: {
          // Django can't parse body without extra property
          'x-requested-with': 'XMLHttpRequest',
        },
      },
    );
    const parsedData = await response.json();
    const data = transformKeys(parsedData, toCamelCase);

    if (response.status === 403) {
      mxData['unsuccess-reason'] = {
        'reached-limit': true,
      };
      track('click-fh-waiver-form-submit', mxData);

      return { data, isReachedLimit: true };
    }

    if (response.status !== 200) {
      mxData['unsuccess-reason'] = {
        'server-error': true,
      };
      track('click-fh-waiver-form-submit', mxData);

      // ToDo: Need to handle edge cases
      throw new Error('Something went wrong');
    }

    mxData.successful = true;
    track('click-fh-waiver-form-submit', mxData);

    return { data };
  } catch (e) {
    mxData['unsuccess-reason'] = {
      'not-found': true,
    };
    track('click-fh-waiver-form-submit', mxData);

    return { isNotFound: true };
  }
};

export const fetchStandaloneWaiver = async (standaloneLinkUuid) => {
  try {
    const response = await fetch(
      `/api/v1/fh-waivers/standalone/${standaloneLinkUuid}/`,
    );
    const parsedData = await response.json();
    const data = transformKeys(parsedData, toCamelCase);

    if (data.company?.processorCountry) {
      enableMixpanel(data.company.processorCountry);
    }

    if (response.status === 429) {
      track('fh-waiver-form-view', { standalone: true, 'reached-limit': true });

      return { data, isReachedLimit: true };
    }

    if (response.status !== 200) {
      track('fh-waiver-form-view', { standalone: true, 'server-error': true });

      throw new Error('Something went wrong');
    }

    if (data.waiver) {
      addSuperProperty({
        'fh-waiver-form-waiver-id': data.waiver.uuid,
      });
    }

    track('fh-waiver-form-view', {
      standalone: true,
      'client-policy': !!data.company?.policies,
      'reached-limit': false,
      'signature-type': toKebabCase(data.waiver?.signatureType || ''),
    });

    return { data };
  } catch (e) {
    track('fh-waiver-form-view', { standalone: true, 'not-found': true });

    return { isNotFound: true };
  }
};

export const signStandaloneWaiver = async (
  standaloneLinkUuid,
  signingToken,
  formData,
) => {
  const mxData = {
    standalone: true,
    'marketing-checkbox': formData.isMarketingChecked,
    'minors-checkbox': formData.isMinorChecked,
    'minors-count': formData.minors.length,
    'sections-count': formData.sectionAcknowledgements?.length || 0,
    successful: false,
  };

  try {
    const body = JSON.stringify(
      transformKeys({ ...formData, signingToken }, toSnakeCase),
    );
    const response = await fetch(
      `/api/v1/fh-waivers/standalone/${standaloneLinkUuid}/signature/`,
      {
        method: 'post',
        body,
        headers: {
          // Django can't parse body without extra property
          'x-requested-with': 'XMLHttpRequest',
        },
      },
    );
    const parsedData = await response.json();
    const data = transformKeys(parsedData, toCamelCase);

    if (response.status === 429) {
      mxData['unsuccess-reason'] = { 'reached-limit': true };
      track('click-fh-waiver-form-submit', mxData);

      return { data, isReachedLimit: true };
    }

    // The one-time signing token expired or was already used.
    if (response.status === 401) {
      mxData['unsuccess-reason'] = { 'token-invalid': true };
      track('click-fh-waiver-form-submit', mxData);

      return { isTokenInvalid: true };
    }

    if (response.status !== 200) {
      mxData['unsuccess-reason'] = { 'server-error': true };
      track('click-fh-waiver-form-submit', mxData);

      throw new Error('Something went wrong');
    }

    mxData.successful = true;
    track('click-fh-waiver-form-submit', mxData);

    return { data };
  } catch (e) {
    mxData['unsuccess-reason'] = { 'not-found': true };
    track('click-fh-waiver-form-submit', mxData);

    return { isNotFound: true };
  }
};

export const redirectToRebooking = (rebookedToBookingUuid) => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  params.set('bookingUuid', rebookedToBookingUuid);
  url.search = params.toString();
  window.location.replace(url.toString());
};

export const createMaxLengthValidator = (charLimit) =>
  MaxLength(
    charLimit,
    interpolate(T(`Maximum of %(maxLength)s characters allowed`), {
      maxLength: charLimit,
    }),
  );

export const createMinValueValidator = (minVal) =>
  Min(
    minVal,
    interpolate(T(`Minimum value is %(min)s`), {
      min: minVal,
    }),
  );
